import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DataTableColumn } from '@atrvd/saas-ui'
import { useAuth } from '@/contexts/AuthContext'
import {
  type ColumnPrefs,
  reconcile,
  readPrefs,
  storageKey,
  toVisibleSet,
  withOrder,
  withVisible,
  writePrefs,
} from '@/lib/columnPrefs'

/**
 * Shape returned by the hook, designed to be spread directly onto the
 * `@atrvd/saas-ui` DataTable's controlled column props.
 */
export interface TableColumnPrefsControls {
  visibleColumns: Set<string>
  onVisibleColumnsChange: (cols: Set<string>) => void
  columnOrder: string[]
  onColumnOrderChange: (order: string[]) => void
}

/**
 * Structural equality for two ColumnPrefs. Used to skip redundant setState
 * during re-reconciliation, which prevents render loops when the effect
 * recomputes an identical result.
 */
function prefsEqual(a: ColumnPrefs, b: ColumnPrefs): boolean {
  return (
    a.version === b.version &&
    a.order.length === b.order.length &&
    a.order.every((k, i) => k === b.order[i]) &&
    a.hidden.length === b.hidden.length &&
    a.hidden.every((k, i) => k === b.hidden[i])
  )
}

/**
 * Persist per-user table column preferences (visibility + order) in
 * localStorage and expose controlled props for the saas-ui DataTable.
 *
 * Preferences are namespaced by the authenticated user's id so different
 * accounts on the same machine keep independent layouts; when no user is
 * present (e.g. pre-auth render) it falls back to an `'anon'` bucket.
 *
 * The hook auto-reconciles whenever the column set changes: removed columns
 * are dropped and newly-added columns appear at the end, hidden by default
 * unless the column declares `defaultVisible: true` (see `reconcile`).
 * `defaultVisible` only means "show on first appearance for returning users whose
 * stored prefs predate the column"; `false` is a no-op and new users see all columns.
 * Storage failures degrade gracefully to in-memory state.
 *
 * @param tableId Stable identifier for the table (part of the storage key).
 * @param columns The table's column definitions (order = default order).
 * @returns Controlled props to spread onto `<DataTable {...controls} />`.
 */
export function useTableColumnPrefs<T>(
  tableId: string,
  columns: DataTableColumn<T>[]
): TableColumnPrefsControls {
  const { user } = useAuth()
  const userId = user?.id ?? 'anon'

  // Default order + universe of known column keys, derived from the config.
  const allKeys = useMemo(() => columns.map((c) => String(c.key)), [columns])
  // Keys that must never be hidden (`disableHide` columns). Threaded into the
  // engine so persistence can never strand a mandatory column in `hidden`
  // (where the DataTable's disabled toggle offers no way to bring it back).
  const mandatoryKeys = useMemo(
    () => columns.filter((c) => c.disableHide).map((c) => String(c.key)),
    [columns]
  )
  // Keys that must arrive VISIBLE when the column is added after a user already
  // saved a layout (`defaultVisible: true`). Without the opt-in, `reconcile`
  // hides every newly-added column, and since a blob is written on first mount
  // that means "hidden for everyone who has ever opened this table" — a column
  // shipped by a ticket would never be seen (DCP-320).
  const optInVisibleKeys = useMemo(
    () => columns.filter((c) => c.defaultVisible).map((c) => String(c.key)),
    [columns]
  )
  // Stable primitive dependencies so effects react to key CONTENT, not the
  // array identity (parents often build `columns` inline on every render).
  // NUL separator so no key value can forge a false signature match.
  const mandatorySignature = mandatoryKeys.join('\u0000')
  const optInSignature = optInVisibleKeys.join('\u0000')
  const keysSignature = allKeys.join('\u0000')

  // Refs mirror the latest key-set values so the effect and the change handlers
  // can read them WITHOUT taking the array identity as a dependency. This is
  // why the handlers below stay identity-stable even for tables that rebuild
  // `columns` inline on every render (the same reason the signatures exist).
  const allKeysRef = useRef(allKeys)
  allKeysRef.current = allKeys
  const mandatoryKeysRef = useRef(mandatoryKeys)
  mandatoryKeysRef.current = mandatoryKeys
  const optInVisibleKeysRef = useRef(optInVisibleKeys)
  optInVisibleKeysRef.current = optInVisibleKeys

  const key = storageKey(userId, tableId)

  // Lazy init: read + reconcile once on first mount.
  const [prefs, setPrefs] = useState<ColumnPrefs>(() =>
    reconcile(readPrefs(key), allKeys, mandatoryKeys, optInVisibleKeys)
  )

  // Mirror of the latest committed prefs. Lets the writer and the handlers work
  // off the current value synchronously instead of a stale render closure --
  // essential on a user switch, where the persisted write must never lag `key`.
  const prefsRef = useRef(prefs)

  // Single writer for prefs: update the ref, the state, and storage together
  // from ONE `next` value. Because state and storage are always written from
  // the same value, localStorage can never be stamped with a different key's
  // layout than the one being committed -- this collapses the former
  // "reconcile setPrefs vs. persist effect" race (where the persist effect ran
  // in the same commit as a key change and wrote the previous user's prefs
  // under the new user's key) into a single atomic step.
  const persist = useCallback(
    (next: ColumnPrefs) => {
      prefsRef.current = next
      setPrefs(next)
      writePrefs(key, next)
    },
    [key]
  )

  // Track the previous storage key so the effect can distinguish a user/table
  // SWITCH (load that key's persisted blob) from a mere column-set change
  // (reconcile the CURRENT in-memory prefs). Starts null so the initial mount
  // counts as a switch and the migrated defaults are persisted once.
  const prevKeyRef = useRef<string | null>(null)

  // Re-reconcile + persist when the storage key or the column set changes. On a
  // key change we load the new key's persisted blob; on a columns-only change
  // we reconcile the in-memory prefs so unsaved customizations survive even
  // when storage is unavailable (private mode / quota) -- re-reading storage
  // there would return null and wipe the user's in-session layout. Reconcile is
  // idempotent, so we only commit on a key switch (the new key must receive its
  // migrated blob) or when the reconciled content actually changed.
  useEffect(() => {
    const keyChanged = prevKeyRef.current !== key
    prevKeyRef.current = key
    const base = keyChanged ? readPrefs(key) : prefsRef.current
    const next = reconcile(
      base,
      allKeysRef.current,
      mandatoryKeysRef.current,
      optInVisibleKeysRef.current
    )
    if (keyChanged || !prefsEqual(prefsRef.current, next)) persist(next)
    // allKeys/mandatoryKeys/optInVisibleKeys are tracked via their stable
    // signatures + refs.
  }, [key, keysSignature, mandatorySignature, optInSignature, persist])

  // Visibility handler: recompute hidden as the complement of the visible set.
  // Reads current prefs/key-sets from refs so its identity depends only on
  // `persist` (i.e. on `key`), never on the columns array's identity.
  const onVisibleColumnsChange = useCallback(
    (cols: Set<string>) => {
      persist(withVisible(prefsRef.current, cols, allKeysRef.current, mandatoryKeysRef.current))
    },
    [persist]
  )

  // Order handler: persist whatever order the DataTable reports (drag or Reset).
  const onColumnOrderChange = useCallback(
    (order: string[]) => {
      persist(withOrder(prefsRef.current, order))
    },
    [persist]
  )

  const visibleColumns = useMemo(() => toVisibleSet(prefs), [prefs])

  return {
    visibleColumns,
    onVisibleColumnsChange,
    columnOrder: prefs.order,
    onColumnOrderChange,
  }
}
